import { Typography } from 'primitives/typography'
import type React from 'react'
import { cloneElement, isValidElement } from 'react'
import { Link, useLocation } from 'router-v6'
import { cn } from '../../lib/utils'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '../../primitives/sidebar'
import type { IRoute } from './types'

interface AppSidebarSettingsProps {
  routes?: IRoute[]
}

export const AppSidebarSettings: React.FC<AppSidebarSettingsProps> = ({ routes = [] }) => {
  const location = useLocation()

  // Get settings routes directly from the nested structure
  const getSettingsRoutes = (routes: IRoute[]): IRoute[] => {
    const settingsRoute = routes.find((route) => route.path === '/settings')
    if (!settingsRoute?.children) return []

    return settingsRoute.children.filter((child) => {
      // Don't filter out routes that have children (like Profile)
      if (child.children && child.children.length > 0) {
        return !child.hidden && !child.notAllowed
      }
      // For routes without children, apply normal filtering
      return !child.hidden && !child.notAllowed
    })
  }

  const settingsRoutes = getSettingsRoutes(routes)

  const renderRoute = (route: IRoute) => {
    const hasChildren = route.children && route.children.length > 0

    // Special case: Profile route should be treated as a simple clickable item
    // even though it has children (which are hidden navigation helpers)
    if (route.path === '/settings/profile') {
      const isActive = location.pathname.startsWith('/settings/profile')
      return (
        <SidebarMenuItem key={route.path}>
          <SidebarMenuButton
            tooltip={typeof route.title === 'string' ? route.title : 'Profile'}
            isActive={isActive}
            asChild
            className="hover:bg-hv1"
          >
            <Link to="/settings/profile/details">
              {isValidElement(route.icon)
                ? cloneElement(route.icon, {
                    strokeWidth: isActive ? 2 : 1.5,
                  } as { strokeWidth: number })
                : route.icon}
              <span>{typeof route.title === 'string' ? route.title : 'Profile'}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    }

    if (hasChildren) {
      // Render collapsible group
      const visibleChildren =
        route.children?.filter((child) => !child.hidden && !child.notAllowed) || []

      // Don't render the group if all children are hidden
      if (visibleChildren.length === 0) {
        return null
      }

      // Check if any child is active to determine group active state
      const isGroupActive = visibleChildren.some((child) => {
        if (child.children) {
          // Check nested children for active state
          return child.children.some(
            (nestedChild) =>
              location.pathname === nestedChild.path ||
              location.pathname.startsWith(nestedChild.path + '/'),
          )
        }
        return location.pathname === child.path || location.pathname.startsWith(child.path + '/')
      })

      return (
        <SidebarMenuItem key={route.path}>
          {/* Group header - not clickable, just a label */}
          <SidebarMenuButton
            tooltip={typeof route.title === 'string' ? route.title : 'Group'}
            className={cn(
              'hover:bg-transparent cursor-default pointer-events-none',
              isGroupActive && 'text-sidebar-accent-foreground',
            )}
          >
            {route.icon}
            <span>{typeof route.title === 'string' ? route.title : 'Group'}</span>
          </SidebarMenuButton>

          {/* Sub-items */}
          <SidebarMenuSub
            className={cn(
              'flex min-w-0 translate-x-px flex-col gap-1 px-0 py-0.5 border-l-0 mb-1 ml-2',
              'group-data-[collapsible=icon]:hidden',
            )}
          >
            {visibleChildren.map((subRoute) => {
              // Handle nested children (like Platform > Phone numbers)
              if (subRoute.children && subRoute.children.length > 0) {
                const nestedChildren = subRoute.children.filter(
                  (child) => !child.hidden && !child.notAllowed,
                )
                return nestedChildren.map((nestedChild) => (
                  <SidebarMenuSubItem key={nestedChild.path}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={
                        location.pathname === nestedChild.path ||
                        location.pathname.startsWith(nestedChild.path + '/')
                      }
                      className="text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-hv1"
                    >
                      <Link to={nestedChild.path} className="flex items-center gap-3">
                        <span className="text-tertiary text-xs leading-none">•</span>
                        <span>
                          {typeof nestedChild.title === 'string' ? nestedChild.title : 'Item'}
                        </span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))
              }

              // Regular sub-route
              return (
                <SidebarMenuSubItem key={subRoute.path}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={
                      location.pathname === subRoute.path ||
                      location.pathname.startsWith(subRoute.path + '/')
                    }
                    className="text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-hv1"
                  >
                    <Link to={subRoute.path} className="flex items-center gap-3">
                      <span className="text-tertiary text-xs leading-none">•</span>
                      <span>{typeof subRoute.title === 'string' ? subRoute.title : 'Item'}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </SidebarMenuItem>
      )
    } else {
      // Render individual item (only if it has a component)
      const isActive =
        location.pathname === route.path || location.pathname.startsWith(route.path + '/')

      return (
        <SidebarMenuItem key={route.path}>
          <SidebarMenuButton
            tooltip={typeof route.title === 'string' ? route.title : 'Settings'}
            isActive={isActive}
            asChild
            className="hover:bg-hv1"
          >
            <Link to={route.path}>
              {isValidElement(route.icon)
                ? cloneElement(route.icon, {
                    strokeWidth: isActive ? 2 : 1.5,
                  } as { strokeWidth: number })
                : route.icon}
              <span>{typeof route.title === 'string' ? route.title : 'Settings'}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    }
  }

  return (
    <>
      <SidebarHeader className="p-4">
        <Typography variant="P/600">Settings</Typography>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsRoutes.map((route) => renderRoute(route)).filter(Boolean)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  )
}
